import { useEffect, useRef, type ReactNode } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

/**
 * The licence consent tickbox above a download table, shared by the post and
 * project-group download modals.
 *
 * `invalid` is set when someone pressed Download without ticking it. The
 * download buttons stay enabled so that press can be observed at all — a
 * disabled button fires no click, and the old greyed-out button gave no reason.
 */
export default function DownloadConsentNotice({
  id,
  accepted,
  onAcceptedChange,
  invalid,
  children,
}: {
  id: string;
  accepted: boolean;
  onAcceptedChange: (next: boolean) => void;
  invalid: boolean;
  children: ReactNode;
}) {
  const labelRef = useRef<HTMLLabelElement>(null);
  const messageId = `${id}-error`;

  // The table under this scrolls; on an 81-file post the notice is off-screen.
  useEffect(() => {
    if (invalid) {
      labelRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [invalid]);

  return (
    <label
      ref={labelRef}
      htmlFor={id}
      className={cn(
        'flex flex-shrink-0 cursor-pointer select-none items-start gap-2 rounded-md border px-3 py-2 text-xs sm:text-sm',
        invalid
          ? 'border-destructive bg-destructive/10'
          : 'border-border bg-muted/40'
      )}
    >
      <Checkbox
        id={id}
        checked={accepted}
        aria-invalid={invalid}
        aria-describedby={invalid ? messageId : undefined}
        onCheckedChange={(checked) => onAcceptedChange(checked === true)}
        className="mt-0.5"
      />
      <span className="leading-snug">
        I understand and agree to abide by the {children}
        {invalid && (
          <span
            id={messageId}
            role="alert"
            className="mt-1 block font-medium text-destructive"
          >
            Tick this to download.
          </span>
        )}
      </span>
    </label>
  );
}
